import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useCompanyLogin } from '../../hooks/useCompanyLogin';

interface CompanyLoginFormProps {
  onForgotPassword?: () => void;
  onRegister?: () => void;
  onStudentLogin?: () => void;
}

export const CompanyLoginForm: React.FC<CompanyLoginFormProps> = ({
  onForgotPassword,
  onRegister,
  onStudentLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    authError,
    errorDetails,
    handleFieldFocus,
  } = useCompanyLogin();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {/* 企業向け説明 */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#EBF8FF', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessIcon sx={{ color: '#2E5BBA', mr: 1 }} />
          <Typography variant="h6" sx={{ color: '#1E3F8A', fontWeight: 600 }}>
            企業管理者・従業員向けログイン
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#374151' }}>
          企業管理者または従業員の方は、こちらからログインしてください。
          採用・スカウト機能、従業員向けAI学習機能をご利用いただけます。
        </Typography>
      </Box>

      {/* エラーアラート */}
      {authError && (
        <Alert
          severity={errorDetails?.code === 'EMAIL_NOT_VERIFIED' ? 'warning' : 'error'}
          sx={{ mb: 2 }}
          action={
            authError.includes('学生として登録') ? (
              <Button
                color="inherit"
                size="small"
                onClick={onStudentLogin}
                sx={{ fontWeight: 'bold' }}
              >
                学生ログインへ
              </Button>
            ) : undefined
          }
        >
          {errorDetails?.code === 'EMAIL_NOT_VERIFIED' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                企業承認待ちです
              </Typography>
              <Typography variant="body2">
                企業登録の承認が完了するまでログインできません。承認完了後、登録メールアドレス宛に通知が届きます。しばらくお待ちください。
              </Typography>
            </>
          ) : authError.includes('学生として登録') ? (
            'このメールアドレスは学生として登録されています。企業登録には別のメールアドレスをご使用ください。'
          ) : (
            authError
          )}
        </Alert>
      )}

      {/* 企業メールアドレス入力 */}
      <TextField
        {...register('email')}
        fullWidth
        label="企業メールアドレス"
        type="email"
        autoComplete="email"
        margin="normal"
        error={!!errors.email}
        helperText={errors.email?.message}
        onFocus={() => handleFieldFocus('email')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* パスワード入力 */}
      <TextField
        {...register('password')}
        fullWidth
        label="パスワード"
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        margin="normal"
        error={!!errors.password}
        helperText={errors.password?.message}
        onFocus={() => handleFieldFocus('password')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* ログイン状態を保持 */}
      <FormControlLabel
        control={<Checkbox {...register('rememberMe')} color="primary" />}
        label={
          <Typography variant="body2" sx={{ color: '#374151' }}>
            ログイン状態を保持する
          </Typography>
        }
        sx={{ mb: 3 }}
      />

      {/* ログインボタン */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{
          height: 48,
          fontSize: '16px',
          fontWeight: 'bold',
          mb: 3,
          background: 'linear-gradient(45deg, #2E5BBA 30%, #1E3F8A 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #1E3F8A 30%, #2E5BBA 90%)',
          },
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'ログイン'
        )}
      </Button>

      {/* フォーム下部のリンク */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          {onForgotPassword && (
            <Link
              component="button"
              variant="body2"
              onClick={onForgotPassword}
              sx={{ mr: 3, color: '#2E5BBA' }}
            >
              パスワードを忘れた方
            </Link>
          )}
          {onRegister && (
            <Link
              component="button"
              variant="body2"
              onClick={onRegister}
              sx={{ color: '#2E5BBA' }}
            >
              企業アカウント登録はこちら
            </Link>
          )}
        </Box>
      </Box>

      {/* 学生ログインへのリンク */}
      {onStudentLogin && (
        <Box
          sx={{ textAlign: 'center', pt: 3, borderTop: '1px solid #E5E7EB' }}
        >
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
            学生の方はこちら
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={onStudentLogin}
            sx={{ fontWeight: 500, color: '#2E5BBA' }}
          >
            学生ログイン画面へ
          </Link>
        </Box>
      )}
    </Box>
  );
};
